/**
 * Preserves marketing attribution query params (UTM, fbclid, etc.)
 * across internal navigation for accurate Meta and analytics tracking.
 */
(function initAttributionPreservation() {
  const STORAGE_KEY = 'kyhip_attribution_params';

  const ATTRIBUTION_PARAM_NAMES = new Set([
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'twclid',
    'li_fat_id',
    '_kx',
    'mc_cid',
    'mc_eid',
  ]);

  /**
   * Returns true when the query param key is used for ad attribution.
   * @param {string} key
   * @returns {boolean}
   */
  function isAttributionParam(key) {
    if (!key) return false;
    if (ATTRIBUTION_PARAM_NAMES.has(key)) return true;
    return key.startsWith('utm_');
  }

  /**
   * Filters a URLSearchParams object down to attribution keys only.
   * @param {URLSearchParams} searchParams
   * @returns {URLSearchParams}
   */
  function extractAttributionParams(searchParams) {
    const params = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (isAttributionParam(key) && value) {
        params.set(key, value);
      }
    });

    return params;
  }

  /**
   * Reads stored attribution params from sessionStorage.
   * @returns {URLSearchParams}
   */
  function loadStoredParams() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? new URLSearchParams(stored) : new URLSearchParams();
    } catch {
      return new URLSearchParams();
    }
  }

  /**
   * Persists attribution params to sessionStorage.
   * @param {URLSearchParams} params
   */
  function saveStoredParams(params) {
    if (!params.toString()) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, params.toString());
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }

  /**
   * Merges stored params with params from the current URL, preferring current URL values.
   * @returns {URLSearchParams}
   */
  function getAttributionParams() {
    const merged = loadStoredParams();
    const currentParams = extractAttributionParams(new URLSearchParams(window.location.search));

    currentParams.forEach((value, key) => {
      merged.set(key, value);
    });

    return merged;
  }

  /**
   * Stores attribution params from the current page URL into sessionStorage.
   */
  function captureAttributionFromCurrentUrl() {
    const currentParams = extractAttributionParams(new URLSearchParams(window.location.search));
    if (!currentParams.toString()) return;

    const merged = loadStoredParams();
    currentParams.forEach((value, key) => {
      merged.set(key, value);
    });

    saveStoredParams(merged);
  }

  /**
   * Returns true when the URL points to the current shop domain.
   * @param {URL} url
   * @returns {boolean}
   */
  function isInternalUrl(url) {
    if (!url || !url.href) return false;
    if (url.protocol === 'mailto:' || url.protocol === 'tel:' || url.protocol === 'javascript:') {
      return false;
    }

    return url.origin === window.location.origin;
  }

  /**
   * Returns true when an anchor should receive attribution params.
   * @param {HTMLAnchorElement | null | undefined} anchor
   * @returns {boolean}
   */
  function isEligibleAnchor(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return false;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }

    if (anchor.hasAttribute('download')) return false;

    try {
      return isInternalUrl(new URL(href, window.location.origin));
    } catch {
      return href.startsWith('/');
    }
  }

  /**
   * Appends stored attribution params to an internal URL string.
   * @param {string} urlString
   * @returns {string}
   */
  function applyAttributionToUrl(urlString) {
    if (!urlString) return urlString;

    const params = getAttributionParams();
    if (!params.toString()) return urlString;

    let url;

    try {
      url = new URL(urlString, window.location.origin);
    } catch {
      return urlString;
    }

    if (!isInternalUrl(url)) return urlString;

    params.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }

  /**
   * Updates existing anchor hrefs so attribution params appear before navigation.
   */
  function augmentExistingAnchors() {
    const anchors = document.querySelectorAll('a[href]');

    anchors.forEach((anchor) => {
      if (!isEligibleAnchor(anchor)) return;

      const attributedUrl = applyAttributionToUrl(anchor.href);
      if (attributedUrl !== anchor.href) {
        anchor.href = attributedUrl;
      }
    });
  }

  /**
   * Intercepts internal link clicks and navigates with attribution params attached.
   * @param {MouseEvent} event
   */
  function handleLinkClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) {
      return;
    }

    const anchor = event.target.closest('a');
    if (!isEligibleAnchor(anchor)) return;

    const attributedUrl = applyAttributionToUrl(anchor.href);
    if (!attributedUrl || attributedUrl === anchor.href) return;

    event.preventDefault();
    window.location.assign(attributedUrl);
  }

  captureAttributionFromCurrentUrl();

  document.addEventListener('click', handleLinkClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', augmentExistingAnchors);
  } else {
    augmentExistingAnchors();
  }

  window.Attribution = {
    applyToUrl: applyAttributionToUrl,
    getParams: getAttributionParams,
  };
})();
